<template>
  <div class="draft-order__container">
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <FeedbackSuccess
          v-if="showableSuccessFeedback"
          :message="noticeMessage"
        />
      </transition>
    </div>
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError
          v-if="showableErrorFeedback"
          :message="feedbackErrorMessage"
        />
      </transition>
    </div>
    <div v-if="!isApp" class="draft-order__header">
      <PageHeadlineWithFourSteps
        :steps="steps"
        :active-step="activeStep"
        title="注文内容の確認"
      />
    </div>
    <div class="draft-order__term-of-sales-container">
      <ConfirmPageSection
        :section-name="'selected-gift'"
        :title="'購入するギフト'"
      >
        <TicketForTermOfSales
          v-for="itemSku in itemSkus"
          :key="itemSku.id"
          :item-name="itemSku.itemName"
          :item-sku-name="
            itemSku.hasMultipleItemSkus ? itemSku.itemSkuName : ''
          "
          :brand-name="itemSku.brandName"
          :is-including-tax="itemSku.isIncludingTax"
          :display-offer-time="itemSku.displayOfferTime"
          :ticket-count="itemSku.quantity"
          :price="itemSku.price"
          :limited-time-sales-ended-at="itemSku.limitedTimeSalesEndedAt"
        />
      </ConfirmPageSection>
      <ConfirmPageSection
        v-if="displayableDiscountPrice"
        :section-name="'discount-price'"
        :title="'ギフトコード値引き分'"
      >
        <p class="draft-order__discount-price__value">
          - ¥{{ discountPrice.toLocaleString() }}
        </p>
      </ConfirmPageSection>
      <ConfirmPageSection :section-name="'payment-price'" :title="'支払総額'">
        <div class="draft-order__payment-price__value">
          <span>¥{{ paymentPrice.toLocaleString() }}</span>
          <span v-if="isIncludingTaxItems">（税込）</span>
        </div>
      </ConfirmPageSection>
      <ConfirmPageSection
        :section-name="'payment-method'"
        :title="'支払手段 / 支払時期'"
      >
        <div class="draft-order__payment-method__value">
          <div v-if="isCreditCardPayment">
            <p>クレジットカード決済</p>
            <p
              class="draft-order__payment-method__credit-card"
              :class="{ 'my-4': isExistCreditCardBrand }"
            >
              <span>カード番号：</span>
              <span class="draft-order__payment-method__credit-card-content">
                <CreditCardBrand
                  v-if="creditCardInfo.card_brand"
                  class="draft-order__payment-method__credit-card-content--brand"
                  :brand-type="creditCardInfo.card_brand"
                  :icon-width="40"
                  :icon-height="27"
                />
              </span>
              <span>{{ creditCardInfo.cc_number }}</span>
            </p>
            <p>
              有効期限：{{
                convertCreditCardExpiredAt(creditCardInfo.cc_expiration)
              }}
            </p>
          </div>
          <div v-else-if="isAmazonpayPayment">
            <p>
              {{ amazonpayPaymentInfo.payment_preferences_payment_descriptor }}
            </p>
            <p id="amazonpay-change-payment">支払い方法を変更</p>
          </div>
          <div v-else class="draft-order__payment-method__name">
            {{ paymentMethod }}
          </div>
        </div>
      </ConfirmPageSection>
      <ConfirmPageSection
        :section-name="'cancel-policy'"
        :title="'キャンセル / 返品等について'"
      >
        <div class="draft-order__cancel-policy__value">
          <p>
            お客様が注文内容の確認画面で注文確定ボタンをクリックした時点で、仕様・注意事項に同意いただいたものとし、お客様のお申し込みが確定したものとさせていただきます。
          </p>
          <p>
            お申し込み確定後のお申し込み内容の変更・キャンセルは以下の場合を除き、承ることができませんので、あらかじめご了承ください。
          </p>
          <ul>
            <li>
              サービスの停止により、電子チケットと商品の交換が不能となった場合
            </li>
            <li>
              システム障害により、電子チケットと商品の交換が不能になった場合
            </li>
            <li>
              対象商品等の販売終了または対象店舗の閉鎖により対象商品等との交換が不能になった場合
            </li>
          </ul>
          <p>
            お問い合わせは、
            <a :href="contactChatbotUrl" target="_blank">こちら</a>
            よりご連絡ください。
          </p>
        </div>
      </ConfirmPageSection>
    </div>
    <form
      id="draft_order_pay_form"
      action="/draft_orders/pay"
      method="post"
      class="draft-order__pay-form"
    >
      <div class="draft-order__pay-form__container">
        <input
          v-if="draftOrderForm.has_alcohol"
          type="hidden"
          name="draft_order_form[over_twenty_check]"
          :value="draftOrderForm.over_twenty_check"
        />
        <input type="hidden" name="authenticity_token" :value="csrfToken" />
        <input
          type="hidden"
          name="draft_order_form[draft_order_id]"
          :value="draftOrderForm.draft_order_id"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_card_session_id]"
          :value="draftOrderForm.gift_card_session_id"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_code_serial_id]"
          :value="draftOrderForm.gift_code_serial_id"
        />
        <input
          type="hidden"
          name="draft_order_form[draft_order_updated_at]"
          :value="draftOrderForm.draft_order_updated_at"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_card_session_updated_at]"
          :value="draftOrderForm.gift_card_session_updated_at"
        />
        <input
          type="hidden"
          name="draft_order_form[gift_code_serial_updated_at]"
          :value="draftOrderForm.gift_code_serial_updated_at"
        />
        <input
          type="hidden"
          name="draft_order_form[has_alcohol]"
          :value="draftOrderForm.has_alcohol"
        />
        <input
          type="hidden"
          name="draft_order_form[amazon_checkout_session_id]"
          :value="draftOrderForm.amazon_checkout_session_id"
        />
        <button
          id="order-submit-button"
          type="submit"
          class="btn btn--primary"
          data-disable-with="注文を確定する"
          :disabled="disableSubmitButton || null"
          @click.prevent="onClickOrderSubmitButton"
        >
          注文を確定する
        </button>
        <div
          v-if="!isApp"
          class="draft-order__pay-form__container__back-create-gift-link"
        >
          <a :href="`/draft_orders/${draftOrderForm.draft_order_id}`">
            決済方法の選択に戻る
          </a>
        </div>
      </div>
      <TextModal
        v-if="isModalActive"
        title-text="ギフトを送信します"
        caption-text="購入完了と同時にギフトを贈ります。相手にはメールでギフトURLが通知されます。"
        primary-button-text="購入してギフトを贈る"
        cancel-button-text="キャンセル"
        @clicked-modal-cover="onClickModalCover"
        @clicked-primary="onClickModalPrimaryButton"
        @clicked-cancel="onClickModalCancelButton"
      />
    </form>
  </div>
</template>
<script>
import axios from 'axios'
import dayjs from 'dayjs'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import PageHeadlineWithFourSteps from '../../molecules/headlines/PageHeadlineWithFourSteps.vue'
import TicketForTermOfSales from '../../atoms/tickets/TicketForTermOfSales.vue'
import ConfirmPageSection from '../..//organism/draft_orders/ConfirmPageSection.vue'
import TextModal from '../../molecules/modals/text-modal.vue'
import CreditCardBrand from '../../molecules/credit_cards/CreditCardBrand.vue'
import {
  GIFT_CARD_FLOW_STEPS,
  GIFT_CARD_FLOW_STEPS_PREVIEW_OPTIONAL
} from '../../../constants/flowLineConstants'

export default {
  components: {
    FeedbackSuccess,
    FeedbackError,
    PageHeadlineWithFourSteps,
    TicketForTermOfSales,
    ConfirmPageSection,
    TextModal,
    CreditCardBrand
  },
  props: {
    noticeMessage: {
      type: String,
      required: false,
      default: ''
    },
    feedbackErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    boxListErrorMessages: {
      validator(value) {
        return value === '' || Array.isArray(value)
      },
      default: () => []
    },
    itemSkuContentData: {
      type: Object,
      required: true
    },
    discountPrice: {
      type: Number,
      required: false,
      default: 0
    },
    paymentPrice: {
      type: Number,
      required: false,
      default: 0
    },
    paymentMethod: {
      type: String,
      required: false,
      default: ''
    },
    isAmazonpayPayment: {
      type: Boolean,
      required: true
    },
    isCreditCardPayment: {
      type: Boolean,
      required: true
    },
    amazonpayPaymentInfo: {
      type: Object,
      required: false,
      default: () => ({})
    },
    creditCardInfo: {
      type: Object,
      required: false,
      default: () => ({})
    },
    giftCodeSerial: {
      type: Object,
      required: false,
      default: () => ({})
    },
    draftOrderForm: {
      type: Object,
      required: true
    },
    needToPay: {
      type: Boolean,
      required: true
    },
    isApp: {
      type: Boolean,
      required: true
    },
    fromWishList: {
      type: Boolean,
      required: true
    },
    fraudDetectionJs: {
      type: String,
      required: true
    },
    itemIdForSbps: {
      type: Number,
      required: false,
      default: 0
    },
    itemNameForSbps: {
      type: String,
      required: false,
      default: ''
    },
    itemCount: {
      type: Number,
      required: false,
      default: 0
    },
    contactChatbotUrl: {
      type: String,
      required: true
    },
    previewOptional: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      showableSuccessFeedback: false,
      showableErrorFeedback: false,
      activeStep: 3,
      // FIXME(gift_card_preview_optional): ABテスト決着後、previewOptionalの分岐を削除する
      steps: this.previewOptional
        ? GIFT_CARD_FLOW_STEPS_PREVIEW_OPTIONAL
        : GIFT_CARD_FLOW_STEPS,
      itemSkus: [],
      csrfToken: '',
      isModalActive: false,
      isSubmitting: false
    }
  },
  computed: {
    hasErrorMessages() {
      return this.boxListErrorMessages && this.boxListErrorMessages.length !== 0
    },
    displayableDiscountPrice() {
      return this.discountPrice > 0
    },
    isEmptyPaymentMehod() {
      return this.paymentMethod.length === 0
    },
    isNotEmptyGiftCodeSerial() {
      return (
        this.giftCodeSerial !== null &&
        Object.keys(this.giftCodeSerial).length !== 0 &&
        this.giftCodeSerial.serial
      )
    },
    disableSubmitButton() {
      return (this.needToPay && this.isEmptyPaymentMehod) || this.isSubmitting
    },
    isIncludingTaxItems() {
      return this.itemSkus.some(sku => sku.isIncludingTax)
    },
    isExistCreditCardBrand() {
      return this.creditCardInfo.card_brand !== null
    }
  },
  mounted() {
    this.showableSuccessFeedback = this.noticeMessage.length !== 0
    this.showableErrorFeedback = this.feedbackErrorMessage.length !== 0
    this.itemSkus = this.itemSkuContentData.data.map(
      itemSku => itemSku.attributes
    )
    this.csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute('content')

    // NOTE: SBPSの不正検知のスクリプトを動的に埋め込む処理
    const fraudDetectionScript = document.createElement('script')
    fraudDetectionScript.setAttribute('src', this.fraudDetectionJs)
    document.head.appendChild(fraudDetectionScript)

    if (this.isAmazonpayPayment) {
      this.setChangeAmazonPayPaymentButton()
    }
  },
  methods: {
    enterSuccessFeedback() {
      setTimeout(() => {
        this.showableSuccessFeedback = false
      }, 3000)
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    setChangeAmazonPayPaymentButton() {
      // eslint-disable-next-line no-undef
      amazon.Pay.bindChangeAction('#amazonpay-change-payment', {
        amazonCheckoutSessionId: this.draftOrderForm.amazon_checkout_session_id,
        changeAction: 'changePayment'
      })
    },
    convertCreditCardExpiredAt(date) {
      return dayjs(date).format('MM/YY')
    },
    showModal() {
      this.isModalActive = true
    },
    closeModal() {
      this.isModalActive = false
    },
    async onClickOrderSubmitButton() {
      if (this.fromWishList) {
        this.showModal()
        event.preventDefault()
      } else {
        await this.submitAfterFraudDetection()
      }
    },
    onClickModalCover() {
      this.closeModal()
    },
    onClickModalCancelButton() {
      this.closeModal()
    },
    async onClickModalPrimaryButton() {
      await this.submitAfterFraudDetection()
    },
    async getSbpsSetting() {
      const res = await axios.get('/credit_cards/sbps_setting')
      return {
        merchantId: res.data.merchant_id,
        serviceId: res.data.service_id
      }
    },
    async submitAfterFraudDetection() {
      this.isSubmitting = true

      if (!this.isCreditCardPayment) {
        // クレジットカード以外の場合、不正検知は対象外なのでそのままフォームを送信する
        return this.submitForm()
      }

      const sbpsSetting = await this.getSbpsSetting()
      // eslint-disable-next-line no-undef
      com_sbps_system_fraud.fraudDetection(
        {
          merchantId: sbpsSetting.merchantId,
          serviceId: sbpsSetting.serviceId,
          ccNumber: null,
          ccExpiration: null,
          securityCode: null,
          custCode:
            this.draftOrderForm.draft_order.cust_code ||
            this.draftOrderForm.current_user.id,
          token: null,
          tokenKey: null,
          orderId: this.draftOrderForm.draft_order.sbps_order_id,
          amount: this.paymentPrice,
          itemId: this.itemIdForSbps,
          itemName: this.itemNameForSbps,
          itemCount: this.itemCount,
          itemCategory: null,
          campaign: null,
          memo: null
        },
        this.afterFraudDetection
      )
    },
    afterFraudDetection(response) {
      if (
        response.fraudRes.result !== 'OK' ||
        response.fraudRes.fraudResponse.code === 30
      ) {
        this.backToDraftOrderAfterFraudDetection(
          response.fraudRes.result,
          response.fraudRes.errorCode
        )
      } else {
        this.submitForm()
      }
    },
    backToDraftOrderAfterFraudDetection(result, errorCode) {
      window.location.href = `/draft_orders/${this.draftOrderForm.draft_order_id}?from_confirm=true&result=${result}&error_code=${errorCode}`
    },
    submitForm() {
      document.querySelector('#draft_order_pay_form').submit()
    }
  }
}
</script>
<style lang="scss" scoped>
.draft-order {
  &__term-of-sales-container {
    background-color: $white;
  }

  &__payment-method {
    &__credit-card {
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }

    &__credit-card-content {
      display: flex;
      justify-content: flex-start;
      align-items: center;

      &--brand {
        margin-right: 8px;
      }
    }
  }

  &__cancel-policy {
    &__value {
      p {
        padding-bottom: 16px;
      }

      ul {
        padding: 0 0 16px 16px;
      }

      li {
        list-style: disc;
      }
    }
  }
}
.ticket--for_term_of_sales:not(:last-child) {
  margin-bottom: 16px;
}
.draft-order-confirm-page-section:not(:last-child) {
  padding-bottom: 16px;
  border-bottom: 1px solid $gray-2;
}
#amazonpay-change-payment {
  margin-top: 10px;
  cursor: pointer;
  color: $link;
}
</style>
